import { createElement, Fragment, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Connection, getConnectedEdges, Handle, NodeProps, Position, useReactFlow } from 'reactflow'
import { JourneyContext, ProjectContext } from '../../../contexts'
import { Alert, Skeleton, Tag } from '../../../ui'
import clsx from 'clsx'
import { getStepType, statIcons, stepCategoryColors } from './JourneyEditorUtils'
import { JourneyStatsContext, wouldCreateCycle } from './JourneyEditorHelpers'
import { KeyIcon } from '../../../ui/icons'
import { formatCompactNumber, snakeToTitle } from '../../../utils'

export const JourneyStepNode = ({
    id,
    data: {
        stepId,
        type: typeName,
        name,
        data,
        data_key,
        stats,
        editing,
        setViewUsersStep,
    } = {},
    selected,
}: NodeProps) => {

    if (!stats) stats = {}

    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [journey] = useContext(JourneyContext)
    const statsStatus = useContext(JourneyStatsContext)
    const { getNode, getEdges } = useReactFlow()

    const type = getStepType(typeName)

    const validateConnection = useCallback((conn: Connection) => {
        if (!type) return false
        if (!conn.source || !conn.target) return false

        // Prevent self-connections
        if (conn.source === conn.target) return false

        // Check single-connection-per-handle unless multiChildSources
        if (!type.multiChildSources) {
            const sourceNode = getNode(conn.source)
            if (!sourceNode) return true
            const existing = getConnectedEdges([sourceNode], getEdges())
            if (existing.filter(e => e.sourceHandle === conn.sourceHandle).length >= 1) return false
            if (existing.some(e => e.target === conn.target)) return false
        }

        // Prevent cycles: check if target can reach source via existing edges
        if (wouldCreateCycle(getEdges(), conn.source, conn.target)) return false

        return true
    }, [type, getNode, getEdges])

    if (!type) {
        return (
            <Alert variant="error" title={t('invalid_step_type')} />
        )
    }

    const statValue = (value?: number) => {
        if (statsStatus === 'loading') return <Skeleton className="stat-loader" />
        if (statsStatus === 'error') return <span className="stat-value" title={t('journey_stats_unavailable')}>-</span>
        const count = value ?? 0
        return <span className="stat-value" title={count.toLocaleString()}>{formatCompactNumber(count)}</span>
    }

    return (
        <>
            {
                !type.hideTopHandle && (
                    <Handle type="target" position={Position.Top} id={'t-' + id} />
                )
            }
            <div
                className={clsx(
                    'journey-step',
                    type.category,
                    selected && 'selected',
                    Array.isArray(type.sources) && 'journey-step-labelled-sources',
                    editing && 'editing',
                )}
            >
                <div className="journey-step-header">
                    <span className={clsx('step-header-icon', stepCategoryColors[type.category])}>
                        {type.icon}
                    </span>
                    <h4 className="step-header-title">{name || t(type.name)}</h4>
                    {type.category !== 'info' && <div className="step-header-stats"
                        onClickCapture={stepId
                            ? () => setViewUsersStep({ stepId, stepType: typeName })
                            : undefined
                        }>
                        <span className="stat">
                            {statValue(stats.completed)}
                            {statIcons.completed}
                        </span>
                        {
                            (typeName === 'delay' || !!stats.delay) && (
                                <span className="stat">
                                    {statValue(stats.delay)}
                                    {statIcons.delay}
                                </span>
                            )
                        }
                        {
                            (typeName === 'action' || !!stats.action) && (
                                <span className="stat">
                                    {statValue(stats.action)}
                                    {statIcons.action}
                                </span>
                            )
                        }
                        {
                            !!stats.error && (
                                <span className="stat stat-error">
                                    {statValue(stats.error)}
                                    {statIcons.error}
                                </span>
                            )
                        }
                    </div>}
                </div>
                <div className="journey-step-body">
                    {
                        type.Describe && createElement(type.Describe, {
                            project,
                            journey,
                            value: data,
                            onChange: () => {},
                        })
                    }
                    {
                        !!data_key && (
                            <div className="data-key" style={{ marginTop: type.Describe ? 10 : undefined }}>
                                <KeyIcon />
                                {data_key}
                            </div>
                        )
                    }
                </div>
            </div>
            {
                !type.hideBottomHandle && (
                    Array.isArray(type.sources)
                        ? type.sources
                        : ['']
                ).map((key, index, arr) => {
                    const left = (((index + 1) / (arr.length + 1)) * 100) + '%'
                    return (
                        <Fragment key={key}>
                            {
                                key && (
                                    <span
                                        className="step-handle-label"
                                        style={{
                                            left,
                                        }}
                                    >
                                        <Tag variant="plain">{snakeToTitle(key)}</Tag>
                                    </span>
                                )
                            }
                            <Handle
                                type="source"
                                position={Position.Bottom} id={key + '-s-' + id}
                                isValidConnection={validateConnection}
                                style={{
                                    left,
                                }}
                            />
                        </Fragment>
                    )
                })
            }
        </>
    )
}

JourneyStepNode.displayName = 'JourneyStepNode'
